import { compact, sortBy, keyBy } from 'lodash';
import { AttributeOption, CallForServiceOption, EventLabelOption } from '@mark43/cad-api';

export function asPriorityOption(
    formEventLabel:
        | {
              label?: string;
              value?: number;
              isOther?: boolean;
          }
        | null
        | undefined
): AttributeOption | undefined {
    if (!formEventLabel) {
        return undefined;
    }
    const { value, label, isOther } = formEventLabel;
    if (value && label) {
        return {
            ...formEventLabel,
            value,
            label,
            isOther: !!isOther,
        };
    }
    return undefined;
}

export function asCallForServiceOption(
    formCallForService:
        | {
              agencyTypeGlobalAttrIds?: (number | undefined)[];
              label?: string;
              priorityAttrId?: number;
              riskLevelAttrId?: number;
              subtitle?: string;
              value?: number;
          }
        | null
        | undefined
): CallForServiceOption | undefined {
    if (!formCallForService) {
        return undefined;
    }
    const { value, label, priorityAttrId, agencyTypeGlobalAttrIds } = formCallForService;
    if (value && label && priorityAttrId && agencyTypeGlobalAttrIds) {
        return {
            ...formCallForService,
            value,
            label,
            priorityAttrId,
            agencyTypeGlobalAttrIds: agencyTypeGlobalAttrIds.filter((id) => !!id) as number[],
        };
    }
    return undefined;
}

export function asEventLabelOption(
    formEventLabel:
        | {
              label?: string;
              locationCautionPriorityGlobalAttrId?: number;
              subtitle?: string;
              value?: number;
          }
        | undefined
): EventLabelOption | undefined {
    if (!formEventLabel) {
        return undefined;
    }
    const { value, label, locationCautionPriorityGlobalAttrId } = formEventLabel;
    if (value && label && locationCautionPriorityGlobalAttrId) {
        return {
            ...formEventLabel,
            value,
            label,
            locationCautionPriorityGlobalAttrId,
        };
    }
    return undefined;
}

export type CallForServiceOptionWithPriority = CallForServiceOption & {
    priorityGlobalAttrId: number;
};

export function toCallForServiceOptionWithPriority(
    callForService: CallForServiceOption | undefined,
    priorityOptions: AttributeOption[]
): CallForServiceOptionWithPriority | undefined {
    if (!callForService) {
        return undefined;
    }
    const callForServiceWithPriority = {
        ...callForService,
        priorityGlobalAttrId: priorityOptions.find(
            (priorityOption) => priorityOption.value === callForService.priorityAttrId
        )?.parentAttributeId,
    };
    if (!callForServiceWithPriority.priorityGlobalAttrId) {
        return undefined;
    }
    return callForServiceWithPriority as CallForServiceOptionWithPriority;
}

export function getHighestPriorityCallForService(
    callForService: CallForServiceOptionWithPriority | undefined,
    additionalCallForServices: CallForServiceOptionWithPriority[]
): CallForServiceOptionWithPriority | undefined {
    const highestPriorityAdditionalCallForService = sortBy(
        additionalCallForServices,
        'priorityGlobalAttrId'
    )[0];
    if (callForService && highestPriorityAdditionalCallForService) {
        // prefer primary call for service priority if there is a tie between primary and additional
        if (
            // lower id means higher priority
            callForService.priorityGlobalAttrId <=
            highestPriorityAdditionalCallForService.priorityGlobalAttrId
        ) {
            return callForService;
        } else {
            return highestPriorityAdditionalCallForService;
        }
    } else if (callForService) {
        return callForService;
    } else if (highestPriorityAdditionalCallForService) {
        return highestPriorityAdditionalCallForService;
    } else {
        return undefined;
    }
}

/**
 * get hightest priority event label option.
 * each eventLabel may or may not have a `priorityAttrId`, which is the attribute value/id from
 * 'priorityOptions'. Find the 'priorityOption' where its `priorityAttrId = priorityOption.value` from `priorityOptions`, if exist.
 * Then based on priorityOption's `parentAttributeId`, which is the `cfsPriorityGlobal`, find the highest priority option.
 * then we can compare the `cfsPriorityGlobal` ids to find the highest priority (e.g. 200001, 200002, smaller value has higher priority)
 */
export function getHighestEventLabelPriorityOption(
    eventLabels: EventLabelOption[],
    priorityOptions: AttributeOption[]
): AttributeOption | undefined {
    const priorityOptionsByValue = keyBy(priorityOptions, 'value');
    const eventLabelToPriorityOption = compact(
        eventLabels.map((eventLabel) => {
            return eventLabel.priorityAttrId
                ? priorityOptionsByValue[eventLabel.priorityAttrId]
                : undefined;
        })
    );
    const highestPriorityEventLabel = eventLabelToPriorityOption.sort(
        (a, b) =>
            (a.parentAttributeId ?? Number.MAX_SAFE_INTEGER) -
            (b.parentAttributeId ?? Number.MAX_SAFE_INTEGER)
    )[0];
    return highestPriorityEventLabel;
}
